import { MessageCircleIcon } from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty"
import { MessageGroup } from "@/components/ui/message"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useAuth } from "@/features/auth/auth-context"
import {
  ConversationRuns,
  ConversationSkeleton,
  groupMessages,
} from "@/features/messages/conversation-parts"
import { MessageComposer } from "@/features/messages/MessageComposer"
import { initialsOf } from "@/features/messages/message-time"
import { useConversationEnd } from "@/features/messages/use-conversation-end"
import {
  directThreadFacts,
  flattenDirectMessages,
  useDirectMessages,
  useMarkDirectRead,
  useSendDirectMessage,
} from "@/features/messages/use-direct-messages"
import { isModuleOff } from "@/features/messages/use-messages"
import { ApiError } from "@/lib/api-error"

/**
 * A conversation between TWO of §2's people — the private half of the messenger.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT IS THE SAME PRODUCT AS THE ROOM, AND THAT IS ENFORCED BY SHARING THE CODE
 * RATHER THAN BY MATCHING IT.
 *
 * The transcript is drawn by `ConversationRuns` / `ConversationGroup` / `Bubble`
 * / `Marker` out of `conversation-parts.tsx` — the very components `GroupRoom`
 * renders, handed the very same `MessageResponse` rows, because the server
 * deliberately answers both routes with one shape. Nothing here draws a bubble of
 * its own. If a private message ever looks different from a room message, that is
 * a bug in one file, not a difference between two.
 *
 * WHAT THIS ADDS, AND ALL IT ADDS: which conversation you are in.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * READ STATE IS PER CONVERSATION AND IT IS NEVER SHARED WITH THE ROOM'S. Each
 * row in the list beside this carries its own `unread` and the watermark is
 * posted per peer — a single mark would clear the CA's unanswered question
 * because the son's message happened to be newer, and there is no "mark unread"
 * anywhere in this system to undo that.
 *
 * EVERY REFUSAL ABOUT ANOTHER PERSON IS A 404 — a stranger, a deactivated
 * account, somebody who may not hold private conversations, and yourself all
 * answer identically and deliberately. So this pane cannot tell those apart from
 * "the module is off", does not try, and says the one true thing it knows: this
 * conversation is not available. (The module being off is decided once, above,
 * by `MessengerScreen`, off the conversation LIST — which is requested
 * unconditionally and 404s for no other reason.)
 *
 * THE COMPOSE BOX IS ALWAYS THERE WHEN THE THREAD IS, and that is not an
 * oversight of the room's `message.send` branch. `message.direct` gates reading
 * and writing TOGETHER on the server, deliberately: the alternative produces
 * somebody who receives private messages he is forbidden to answer. So there is
 * no read-only state to draw here — if you can see this thread you can write in
 * it, and the server checks the capability at BOTH ends of the pair so nobody can
 * be dragged into a side channel either.
 */
export function DirectThread({
  peerUserId,
  peerName,
}: {
  peerUserId: number
  peerName: string | null
}): React.JSX.Element {
  const { t } = useTranslation()
  const { user } = useAuth()
  const query = useDirectMessages(peerUserId)
  const markRead = useMarkDirectRead(peerUserId)
  const send = useSendDirectMessage(peerUserId)

  const messages = flattenDirectMessages(query.data)
  const facts = directThreadFacts(query.data)
  const total = facts.total

  /*
   * "MINE" IS THE SESSION'S USER ID AGAINST THE AUTHOR'S — never a role (§2.1),
   * and never "the author is not the peer" either. Both would work today with two
   * participants and both would be wrong for the reason that matters: the rule is
   * about who is reading, and the id is what says so.
   */
  const groups = groupMessages(messages, user?.id ?? null)

  /*
   * THE WATERMARK MOVES ON WHAT WAS SEEN, NEVER ON WHAT WAS FETCHED — the room's
   * argument, and it costs the same thing to get wrong here.
   *
   * A page is fifty messages and the box shows six or seven. Posting the newest
   * id when the DATA arrives would clear a badge of twenty having painted only
   * the oldest seven, and there is no per-message read state, no "mark unread"
   * and no deletion, so a badge cleared wrongly is cleared for ever. So it waits
   * for the end of the conversation to genuinely reach the viewport, once per id
   * (`>=`, so a smaller id from a "load older" page can never be re-posted).
   *
   * THE REF IS SAFE ACROSS PEOPLE BECAUSE THIS COMPONENT IS KEYED ON THE PEER —
   * see the `key` at the call site in `MessengerScreen`. Without that, the son's
   * newest id would sit in this ref and silently refuse to mark the CA's
   * conversation read.
   */
  const newestId = messages.length === 0 ? null : (messages[messages.length - 1]?.id ?? null)
  const { endRef, newestOnScreen } = useConversationEnd(newestId)
  const markedRef = React.useRef(0)
  const mark = markRead.mutate
  React.useEffect(() => {
    if (newestId === null || !newestOnScreen) return
    if (markedRef.current >= newestId) return
    markedRef.current = newestId
    mark(newestId)
  }, [newestId, newestOnScreen, mark])

  /*
   * A 404 ON THE THREAD IS NOT NECESSARILY "SWITCHED OFF" — it is also every
   * refusal about another person, which the server answers identically on
   * purpose. So this says the one thing that is true in all of those cases rather
   * than guessing which one happened.
   */
  const notAvailable = query.isError && isModuleOff(query.error)

  /* The list's name for this person is preferred; page one of the thread is the
   * fallback for a deep link that arrived before the list did. */
  const name = peerName ?? facts.peerName

  return (
    <Card className="flex min-w-0 flex-col overflow-hidden py-0" data-testid="direct-thread">
      <header className="flex items-center gap-2.5 border-b p-3 md:p-4">
        <Avatar size="sm" aria-hidden="true">
          <AvatarFallback>{initialsOf(name ?? "")}</AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-col">
          <h2 className="truncate text-sm font-semibold" data-testid="direct-peer-name">
            {name ?? t("chat.directUnknownPeer")}
          </h2>
          <p className="text-muted-foreground truncate text-xs">{t("chat.directRowHelp")}</p>
        </div>
      </header>

      {notAvailable ? (
        <Alert className="m-3 w-auto md:m-4" data-testid="direct-unavailable">
          <AlertDescription>{t("chat.directUnavailable")}</AlertDescription>
        </Alert>
      ) : null}

      {query.isError && !notAvailable ? (
        <Alert variant="destructive" className="m-3 w-auto md:m-4">
          <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {query.error instanceof ApiError ? query.error.message : t("err.somethingWentWrong")}
            </span>
            <Button
              variant="outline"
              onClick={() => {
                void query.refetch()
              }}
            >
              {t("btn.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      <ScrollArea className="h-[60vh] min-h-64">
        <MessageGroup className="gap-3 p-3 md:p-4">
          {query.isPending ? <ConversationSkeleton /> : null}

          {/* "LOAD OLDER" AT THE TOP, WHICH IS WHERE OLDER IS. */}
          {query.hasNextPage ? (
            <div className="flex justify-center">
              <Button
                variant="outline"
                disabled={query.isFetchingNextPage}
                onClick={() => {
                  void query.fetchNextPage()
                }}
              >
                {t("chat.loadOlder")}
              </Button>
            </div>
          ) : null}

          {/*
            * "YOU HAVE NOT SPOKEN YET" IS A CLAIM ABOUT THE CONVERSATION, SO IT
            * NEEDS THE CONVERSATION — `query.data !== undefined`, never
            * `!isPending`, which is also false when the request FAILED. An
            * unstarted conversation is a 200 with an empty list (the server
            * refuses to conflate it with "no such person"), so this state is real
            * and reachable rather than defensive.
            */}
          {query.data !== undefined && total === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <MessageCircleIcon />
                </EmptyMedia>
                <EmptyTitle>{t("chat.directEmptyTitle")}</EmptyTitle>
                <EmptyDescription>{t("chat.directEmptyHelp")}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : null}

          {/*
            * THE SHARED TRANSCRIPT. Same runs, same bubbles, same day rules, same
            * attachment rules as the room — see `conversation-parts.tsx`.
            */}
          <ConversationRuns groups={groups} />

          {/*
            * The end of the conversation, as an element: no text, no height. It
            * exists so the browser can answer where to scroll on opening, and
            * whether the newest message is genuinely on screen before the
            * watermark is allowed past it.
            */}
          <div
            ref={endRef}
            data-testid="conversation-end"
            aria-hidden="true"
            className="h-px w-full shrink-0"
          />
        </MessageGroup>
      </ScrollArea>

      {/*
        * THE COMPOSER IS THE ROOM'S, HANDED A DIFFERENT SENDER. One box, one set
        * of rules about length, offline and Ctrl+Enter, one attachment picker —
        * and the only thing that differs between a private message and a room
        * message is where it is posted, which is exactly what `sender` carries.
        */}
      <MessageComposer sender={send} />
    </Card>
  )
}
